import { useEffect, useRef, useCallback } from 'react';

const VERSION_CHECK_INTERVAL = 60 * 1000; // Check every minute
const VERSION_KEY = 'app_build_version';
const VERSION_URL = '/build-version.json';

interface BuildVersion {
  version: string;
  timestamp: number;
  commit?: string;
}

/**
 * Hook to check for app updates and force refresh when needed.
 * Works around mobile browser caching issues with PWAs.
 */
export function useVersionCheck() {
  const lastCheckRef = useRef<number>(0);
  const currentVersionRef = useRef<string | null>(null);

  const checkForUpdates = useCallback(async (forceReload = false) => {
    try {
      // Fetch with cache busting
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) return;

      const data: BuildVersion = await response.json();
      const newVersion = data.version;

      // Get stored version
      const storedVersion = localStorage.getItem(VERSION_KEY);

      // First load - store version
      if (!storedVersion) {
        localStorage.setItem(VERSION_KEY, newVersion);
        currentVersionRef.current = newVersion;
        console.log(`[Version] Initial version: ${newVersion}`);
        return;
      }

      // Version mismatch - new deployment detected
      if (storedVersion !== newVersion) {
        console.log(`[Version] Update detected: ${storedVersion} → ${newVersion}`);

        // Clear all caches
        if ('caches' in window) {
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map((key) => caches.delete(key)));
          console.log(`[Version] Cleared ${cacheKeys.length} caches`);
        }

        // Unregister service workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
          console.log(`[Version] Unregistered ${registrations.length} service workers`);
        }

        // Store new version
        localStorage.setItem(VERSION_KEY, newVersion);

        // Force reload
        if (forceReload || window.confirm('New version available! Reload to update?')) {
          window.location.reload();
        }
      } else {
        currentVersionRef.current = newVersion;
      }
    } catch (error) {
      // Ignore errors (offline, etc.)
      console.log('[Version] Check failed:', error);
    }
  }, []);

  // Force refresh function (for manual trigger)
  const forceRefresh = useCallback(async () => {
    console.log('[Version] Force refresh triggered');

    // Clear all caches
    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }

    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    // Clear version to force re-check
    localStorage.removeItem(VERSION_KEY);

    // Hard reload
    window.location.reload();
  }, []);

  useEffect(() => {
    // Initial check after a short delay
    const initialCheck = setTimeout(() => {
      checkForUpdates();
    }, 2000);

    // Periodic check
    const interval = setInterval(() => {
      checkForUpdates();
    }, VERSION_CHECK_INTERVAL);

    // Check when page becomes visible (user returns to tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // Only check if more than 30 seconds since last check
        if (now - lastCheckRef.current > 30000) {
          lastCheckRef.current = now;
          checkForUpdates();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(initialCheck);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkForUpdates]);

  return { forceRefresh, checkForUpdates };
}
